// The portal's lender records. Two ways (LH-B1):
//   fixtures (default, every test): one zustand store persisted in this browser; "Reset demo data" puts the shipped
//     fixtures back. Every change writes a History line and, where the backend would, an Alert and an event.
//   the API (VITE_HUB_API=1): the same store is a cache of the Lender Hub API. An action still changes the screen at
//     once, then its call goes to the API in order (the wizard saves, then activates); the API's answer replaces the
//     local copy and History / Alerts are read back, so what shows is always the Hub's. A refused call shows why and
//     puts the Hub's copy back.
// No personal data of applicants lives here: the Hub holds lenders, not leads.
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { notify } from '@/kit/ui'
import { ALERTS, HISTORY, LENDERS, USERS } from '@/fixtures/data'
import { api, ApiError, apiMode, hub } from '@/lib/hubApi'
import { useSessionStore } from '@/store/sessionStore'
import type { Alert, EnvVar, HistoryEntry, Lender, LenderStatusChanged, Manifest, Product, User } from '@/types'

const nowIso = () => new Date().toISOString()
const newId = (p: string) => `${p}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`

export interface HubState {
  lenders: Lender[]
  history: HistoryEntry[]
  alerts: Alert[]
  users: User[]
  /** the events the backend would publish, newest first (shown on Hub API & consumers) */
  events: LenderStatusChanged[]

  lender: (slug: string) => Lender | undefined
  setLenderStatus: (slug: string, on: boolean, reason: string, who: string) => void
  updateLender: (slug: string, patch: Partial<Lender>, who: string, what: string) => void
  updateProduct: (slug: string, productSlug: string, patch: Partial<Product>, who: string, what: string) => void
  addProduct: (slug: string, product: Product, who: string) => void
  addLender: (lender: Lender, who: string) => void
  activateLender: (slug: string, who: string) => void
  acknowledgeAlert: (id: string, who: string) => void
  /** a History line from another store (fixtures; on the API since LH-B3 the API writes every line itself) */
  addHistory: (who: string, lenderSlug: string, where: string, what: string) => void
  /** an Alert from another store (fixtures; on the API the API raises every alert itself) */
  addAlert: (alert: Omit<Alert, 'id' | 'at' | 'acknowledgedAt'>) => void
  /** a silent change of lender fields kept in step by another store (live / draft batch); no History line */
  patchLender: (slug: string, patch: Partial<Lender>) => void
  /** tab 5 · Env variables: set a value (UAT or prod), add a variable by hand, remove one */
  setEnvVar: (slug: string, name: string, patch: Partial<Pick<EnvVar, 'uat' | 'prod' | 'secret'>>, who: string) => void
  removeEnvVar: (slug: string, name: string, who: string) => void
  /** discovery found the variables the code reads: new names appear in the tab with empty values; usedIn is refreshed */
  syncEnvFromManifest: (slug: string, manifest: Manifest, who: string) => void
  reset: () => void
}

function entry(who: string, lenderSlug: string, where: string, what: string): HistoryEntry {
  return { id: newId('h'), at: nowIso(), who, lenderSlug, where, what }
}

// ---- the API side ----------------------------------------------------------------------------------------------

/**
 * The lender fields PATCH /lenders/:slug takes: the same list as api2 src/modules/lenders/lender-record.ts PATCHABLE (a test compares them; a
 * field missing here is silently not saved on the Hub). `workflows` is derived by the API since LH-B3.
 */
export const PATCHABLE: (keyof Lender)[] = ['name', 'legalName', 'rbiCor', 'website', 'partnerType', 'connection', 'lenders', 'grievance', 'brand', 'shortLine', 'links', 'env', 'ipWhitelist', 'usedBy', 'liveBatch', 'draftBatch', 'appName', 'logoPermission', 'creditCheckNote', 'linkParams']
const pick = (patch: Partial<Lender>) => Object.fromEntries(Object.entries(patch).filter(([k]) => PATCHABLE.includes(k as keyof Lender))) as Partial<Lender>

let queue: Promise<unknown> = Promise.resolve()

/** Resolves when every call sent so far has been answered (tests, and the screens that wait for the Hub). */
export const whenSynced = () => queue

/** Other stores that read the Hub at sign-in and after a refusal (the code store: batches, APIs, captured codes). */
export const extraLoads: Array<(slugs: string[]) => Promise<unknown>> = []

/** Reads everything the lender screens need from the API into the stores. */
export async function loadFromHub(opts: { users?: boolean; me?: User } = {}) {
  const [lenders, history, alerts, events] = await Promise.all([hub.lenders(), hub.history(), hub.alerts(), hub.events()])
  // the users the screens pick from: the active ones (Users & roles reads every user itself, S-3)
  const users = opts.users ? (await hub.users()).filter((u) => (u as { status?: string }).status !== 'disabled') : opts.me ? [opts.me] : useHubStore.getState().users
  useHubStore.setState({ lenders, history, alerts, events, users })
  await Promise.all(extraLoads.map((load) => load(lenders.map((l) => l.slug))))
}

/** History, alerts and events read again (after a change the API made its own lines for). */
export async function refreshLog() {
  const [history, alerts, events] = await Promise.all([hub.history(), hub.alerts(), hub.events()])
  useHubStore.setState({ history, alerts, events })
}

/** One lender read again (its live / draft batch and tab 5 follow the code the API saved). */
export async function refreshLender(slug: string) {
  replaceLender(await api<Lender>('GET', `/lenders/${encodeURIComponent(slug)}`))
}

/** API mode: runs the call after the earlier ones, applies the answer, reads History / Alerts back; a refusal reloads. */
export function remote<T>(call: () => Promise<T>, apply?: (res: T) => void): void {
  if (!apiMode()) return
  queue = queue.then(async () => {
    try {
      const res = await call()
      apply?.(res)
      await refreshLog()
    } catch (err) {
      // signed out meanwhile (idle 30 minutes, signed out elsewhere): back to the sign-in screen
      if (err instanceof ApiError && err.status === 401) { useSessionStore.setState({ status: 'signed-out', view: null }); return }
      notify.error('Not saved on the Hub', err instanceof Error ? err.message : String(err))
      await loadFromHub().catch(() => {})
    }
  })
}

const replaceLender = (l: Lender) => useHubStore.setState((s) => ({ lenders: s.lenders.some((x) => x.slug === l.slug) ? s.lenders.map((x) => (x.slug === l.slug ? l : x)) : [...s.lenders, l] }))
const lenderCall = (method: string, path: string, body?: unknown) => remote(() => api<Lender>(method, path, body), replaceLender)
const enc = encodeURIComponent

export const useHubStore = create<HubState>()(
  persist(
    (set, get) => ({
      // on the API nothing of the fixtures is ever shown: empty until the Hub answers (SessionGate loads it)
      lenders: apiMode() ? [] : LENDERS,
      history: apiMode() ? [] : HISTORY,
      alerts: apiMode() ? [] : ALERTS,
      users: apiMode() ? [] : USERS,
      events: [],

      lender: (slug) => get().lenders.find((l) => l.slug === slug),

      setLenderStatus: (slug, on, reason, who) => {
        set((s) => {
          const at = nowIso()
          const lenders = s.lenders.map((l) => {
            if (l.slug !== slug) return l
            const workflows = l.workflows.map((w) =>
              on ? (w.state === 'suspended' ? { ...w, state: 'live' as const } : w) : w.state === 'live' ? { ...w, state: 'suspended' as const } : w,
            )
            return { ...l, status: (on ? 'active' : 'off') as Lender['status'], activeSince: on ? at.slice(0, 10) : l.activeSince, offReason: on ? null : reason, workflows, updatedAt: at }
          })
          const l = lenders.find((x) => x.slug === slug)!
          const what = on ? `Activated everywhere: “${reason}”` : `Deactivated everywhere: “${reason}”`
          const alert: Alert = {
            id: newId('a'), at, lenderSlug: slug, severity: on ? 'info' : 'warning',
            kind: on ? 'info' : 'lender_off',
            text: on ? `Lender activated by ${who} (“${reason}”); endpoints resumed.` : `Lender deactivated by ${who} (“${reason}”); endpoints suspended; website and app hide it within 60 s.`,
            acknowledgedAt: null,
          }
          const ev: LenderStatusChanged = { slug, status: l.status, reason, at, by: who }
          return { lenders, history: [entry(who, slug, 'Lender', what), ...s.history], alerts: [alert, ...s.alerts], events: [ev, ...s.events] }
        })
        lenderCall('POST', `/lenders/${enc(slug)}/status`, { on, reason })
      },

      updateLender: (slug, patch, who, what) => {
        set((s) => ({
          lenders: s.lenders.map((l) => (l.slug === slug ? { ...l, ...patch, updatedAt: nowIso() } : l)),
          history: [entry(who, slug, 'Lender info', what), ...s.history],
        }))
        lenderCall('PATCH', `/lenders/${enc(slug)}`, { patch: pick(patch), what })
      },

      updateProduct: (slug, productSlug, patch, who, what) => {
        set((s) => ({
          lenders: s.lenders.map((l) =>
            l.slug === slug ? { ...l, products: l.products.map((p) => (p.slug === productSlug ? { ...p, ...patch } : p)), updatedAt: nowIso() } : l,
          ),
          history: [entry(who, slug, `Product ${productSlug}`, what), ...s.history],
        }))
        const rest = { ...patch }
        delete rest.slug
        lenderCall('PATCH', `/lenders/${enc(slug)}/products/${enc(productSlug)}`, { patch: rest, what })
      },

      addProduct: (slug, product, who) => {
        set((s) => ({
          lenders: s.lenders.map((l) => (l.slug === slug ? { ...l, products: [...l.products, product], updatedAt: nowIso() } : l)),
          history: [entry(who, slug, `Product ${product.slug}`, `Added “${product.name}”`), ...s.history],
        }))
        lenderCall('POST', `/lenders/${enc(slug)}/products`, product)
      },

      addLender: (lender, who) => {
        set((s) => ({
          lenders: [...s.lenders.filter((l) => l.slug !== lender.slug), lender],
          history: [entry(who, lender.slug, 'Lender', s.lenders.some((l) => l.slug === lender.slug) ? 'Draft saved' : 'Draft created'), ...s.history],
        }))
        lenderCall('POST', '/lenders', lender)
      },

      activateLender: (slug, who) => {
        set((s) => {
          const at = nowIso()
          return {
            lenders: s.lenders.map((l) => (l.slug === slug ? { ...l, status: 'active' as const, activeSince: at.slice(0, 10), offReason: null, usedBy: ['website', 'app'], updatedAt: at } : l)),
            history: [entry(who, slug, 'Lender', 'Activated (onboarding complete)'), ...s.history],
            events: [{ slug, status: 'active' as const, reason: null, at, by: who }, ...s.events],
          }
        })
        lenderCall('POST', `/lenders/${enc(slug)}/activate`)
      },

      acknowledgeAlert: (id, who) => {
        set((s) => ({
          alerts: s.alerts.map((a) => (a.id === id ? { ...a, acknowledgedAt: nowIso() } : a)),
          history: [entry(who, s.alerts.find((a) => a.id === id)?.lenderSlug ?? '', 'Alerts', `Acknowledged: ${s.alerts.find((a) => a.id === id)?.text.slice(0, 60)}…`), ...s.history],
        }))
        remote(() => api('POST', `/alerts/${enc(id)}/ack`))
      },

      // on the API the Hub writes History and raises Alerts with the change itself (read back after every call)
      addHistory: (who, lenderSlug, where, what) => {
        if (apiMode()) return
        set((s) => ({ history: [entry(who, lenderSlug, where, what), ...s.history] }))
      },

      addAlert: (alert) => {
        if (apiMode()) return
        set((s) => ({ alerts: [{ ...alert, id: newId('a'), at: nowIso(), acknowledgedAt: null }, ...s.alerts] }))
      },

      patchLender: (slug, patch) => {
        const l = get().lenders.find((x) => x.slug === slug)
        if (!l || Object.entries(patch).every(([k, v]) => JSON.stringify(l[k as keyof Lender]) === JSON.stringify(v))) return
        set((s) => ({ lenders: s.lenders.map((x) => (x.slug === slug ? { ...x, ...patch } : x)) }))
        const fields = pick(patch)
        if (Object.keys(fields).length) lenderCall('PATCH', `/lenders/${enc(slug)}`, { patch: fields })
      },

      setEnvVar: (slug, name, patch, who) => {
        const l = get().lenders.find((x) => x.slug === slug)
        if (!l) return
        const at = nowIso()
        const exists = l.envVars.some((v) => v.name === name)
        const envVars = exists
          ? l.envVars.map((v) => (v.name === name ? { ...v, ...patch, updatedAt: at, updatedBy: who } : v))
          : [...l.envVars, { name, uat: null, prod: null, secret: /KEY|SECRET|TOKEN|PASS|AUTH/i.test(name), source: 'manual' as const, usedIn: [], ...patch, updatedAt: at, updatedBy: who }]
        const what = exists ? `${name}: ${Object.keys(patch).filter((k) => k !== 'secret').map((k) => `${k} value ${patch[k as 'uat' | 'prod'] === null || patch[k as 'uat' | 'prod'] === '' ? 'cleared' : 'set'}`).join(', ') || 'changed'}` : `${name} added`
        set((s) => ({ lenders: s.lenders.map((x) => (x.slug === slug ? { ...x, envVars } : x)), history: [entry(who, slug, 'Env variables', what), ...s.history] }))
        remote(
          () => api<EnvVar>('PUT', `/lenders/${enc(slug)}/env/${enc(name)}`, patch),
          (v) => useHubStore.setState((s) => ({ lenders: s.lenders.map((x) => (x.slug === slug ? { ...x, envVars: x.envVars.map((e) => (e.name === v.name ? v : e)) } : x)) })),
        )
      },
      removeEnvVar: (slug, name, who) => {
        const l = get().lenders.find((x) => x.slug === slug)
        if (!l) return
        set((s) => ({ lenders: s.lenders.map((x) => (x.slug === slug ? { ...x, envVars: x.envVars.filter((v) => v.name !== name) } : x)), history: [entry(who, slug, 'Env variables', `${name} removed`), ...s.history] }))
        remote(() => api('DELETE', `/lenders/${enc(slug)}/env/${enc(name)}`))
      },
      syncEnvFromManifest: (slug, manifest, who) => {
        const l = get().lenders.find((x) => x.slug === slug)
        if (!l) return
        const at = nowIso()
        let changed = false
        const envVars = l.envVars.map((v) => {
          const found = manifest.env.find((e) => e.name === v.name)
          if (!found || JSON.stringify(found.files) === JSON.stringify(v.usedIn)) return v
          return { ...v, usedIn: found.files }
        })
        const added: string[] = []
        for (const e of manifest.env) {
          if (envVars.some((v) => v.name === e.name)) continue
          const hubOwn = e.name === 'LENDER_BASE_URL' || e.name === 'LENDER_TOKEN'
          envVars.push({ name: e.name, uat: e.name === 'LENDER_BASE_URL' ? l.env.uatBaseUrl || null : null, prod: e.name === 'LENDER_BASE_URL' ? l.env.prodBaseUrl || null : null, secret: /KEY|SECRET|TOKEN|PASS|AUTH/i.test(e.name), source: hubOwn ? 'hub' : 'code', usedIn: e.files, updatedAt: at, updatedBy: who })
          added.push(e.name)
          changed = true
        }
        if (changed || envVars.some((v, i) => v !== l.envVars[i])) set((s) => ({ lenders: s.lenders.map((x) => (x.slug === slug ? { ...x, envVars } : x)) }))
        if (added.length) set((s) => ({ history: [entry(who, slug, 'Env variables', `Found in the code, waiting for a value: ${added.join(', ')}`), ...s.history] }))
        remote(
          () => api<EnvVar[]>('POST', `/lenders/${enc(slug)}/env/sync`, { manifest }),
          (vars) => useHubStore.setState((s) => ({ lenders: s.lenders.map((x) => (x.slug === slug ? { ...x, envVars: vars } : x)) })),
        )
      },
      reset: () => {
        if (apiMode()) { void loadFromHub(); return }
        set({ lenders: LENDERS, history: HISTORY, alerts: ALERTS, users: USERS, events: [] })
      },
    }),
    // the API's copy is never kept in this browser: it is read again at every sign-in
    { name: apiMode() ? 'lender-hub-portal-api' : 'lender-hub-portal', version: 1, partialize: (s) => (apiMode() ? ({} as Partial<HubState>) : s) },
  ),
)
